import React, { useEffect, useState, useRef } from 'react';
import { listenConversation, listenMessages, replyToAdmin, markConversationRead } from './messages';

// User-facing inbox. The icon renders ONLY while an admin conversation is open;
// it disappears the moment an admin closes it. Users can read + reply, never start
// a conversation.
export default function MessagesInbox({ currentUser }) {
  const [conv, setConv] = useState(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef();

  useEffect(() => {
    if (!currentUser?.uid) { setConv(null); return; }
    return listenConversation(currentUser.uid, setConv);
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!open || !currentUser?.uid) return;
    const unsub = listenMessages(currentUser.uid, setMessages);
    markConversationRead().catch(() => {});
    return unsub;
  }, [open, currentUser?.uid]);

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ block: 'end' }); }, [messages, open]);

  // If an admin closes the conversation while the panel is open, close it.
  useEffect(() => { if (conv && conv.open !== true) setOpen(false); }, [conv]);

  if (!conv || conv.open !== true) return null;

  const send = async () => {
    if (!draft.trim() || busy) return;
    setBusy(true);
    try { await replyToAdmin(draft.trim()); setDraft(''); }
    catch (e) { console.error('reply failed', e); }
    setBusy(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        title="Messages from the Your Top 8 team"
        style={{
          background: 'none', border: 'none', cursor: 'pointer', position: 'relative',
          padding: '6px', color: open ? 'var(--primary)' : 'var(--text-muted)',
          display: 'flex', alignItems: 'center',
        }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 12h-6l-2 3h-4l-2-3H2" />
          <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
        </svg>
        {conv.unreadForUser && (
          <span style={{
            position: 'absolute', top: 3, right: 3, background: 'var(--primary)',
            width: 9, height: 9, borderRadius: 999,
          }} />
        )}
      </button>

      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 490, background: 'transparent' }} onClick={() => setOpen(false)} />
          <div style={{
            position: 'fixed', top: 64, right: 12, zIndex: 491,
            width: 'min(360px, calc(100vw - 24px))', maxHeight: '70vh',
            background: 'var(--card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Your Top 8 team</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Official messages. Reply here.</div>
            </div>

            <div style={{ padding: 12, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
              {messages.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', margin: '12px 0' }}>No messages yet.</p>
              ) : messages.map(m => (
                <div key={m.id} style={{ alignSelf: m.from === 'user' ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
                  <div style={{
                    padding: '8px 11px', borderRadius: 12, fontSize: 13.5, lineHeight: 1.45, whiteSpace: 'pre-wrap',
                    background: m.from === 'user' ? 'var(--primary)' : 'var(--surface2)',
                    color: m.from === 'user' ? '#0d0d0d' : 'var(--text)',
                  }}>{m.body}</div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <div style={{ padding: 10, borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
              <input
                className="input"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Write a reply…"
                style={{ flex: 1 }}
              />
              <button className="btn btn-primary btn-sm" onClick={send} disabled={busy || !draft.trim()}>Send</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
